import { Facebook, Instagram, Youtube, Share2, X } from 'lucide-react';
import { useState } from 'react';

const SITE_URL = 'https://www.kazajahstudio.com';

export function ShareButtons({ title, description, className = '' }) {
  const [showMenu, setShowMenu] = useState(false);
  const shareText = `${title} - ${description || 'KAZAJAH STUDIO / LABEL VERNABEL'}`;
  const shareUrl = SITE_URL;

  const shareLinks = [
    { name: 'Facebook', color: 'hover:bg-blue-600', icon: <Facebook size={16} />, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}` },
    { name: 'WhatsApp', color: 'hover:bg-green-600', icon: <span className="text-sm font-bold">W</span>, url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}` },
    { name: 'Twitter', color: 'hover:bg-sky-500', icon: <X size={16} />, url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
    { name: 'Copier', color: 'hover:bg-zinc-600', icon: <Share2 size={16} />, action: () => { navigator.clipboard.writeText(shareUrl); setShowMenu(false); } },
  ];

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        data-testid="share-button"
        className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-zinc-400 hover:text-white transition-all"
        title="Partager"
      >
        <Share2 size={16} />
      </button>
      {showMenu && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl p-2 min-w-[160px]" data-testid="share-menu">
          {shareLinks.map((link) => (
            link.action ? (
              <button key={link.name} onClick={link.action}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-300 rounded-lg ${link.color} transition-all`}>
                {link.icon} {link.name}
              </button>
            ) : (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className={`flex items-center gap-3 px-3 py-2 text-sm text-zinc-300 rounded-lg ${link.color} transition-all`}>
                {link.icon} {link.name}
              </a>
            )
          ))}
        </div>
      )}
    </div>
  );
}

export function ArtistSocials({ socials, size = 16 }) {
  if (!socials) return null;
  const hasAny = socials.facebook || socials.instagram || socials.youtube || socials.spotify;
  if (!hasAny) return null;

  return (
    <div className="flex items-center gap-2" data-testid="artist-socials">
      {socials.facebook && (
        <a href={socials.facebook} target="_blank" rel="noopener noreferrer"
          className="p-1.5 rounded-full bg-white/5 hover:bg-blue-500/20 text-zinc-500 hover:text-blue-400 transition-all"
          data-testid="artist-facebook-link">
          <Facebook size={size} />
        </a>
      )}
      {socials.instagram && (
        <a href={socials.instagram} target="_blank" rel="noopener noreferrer"
          className="p-1.5 rounded-full bg-white/5 hover:bg-pink-500/20 text-zinc-500 hover:text-pink-400 transition-all"
          data-testid="artist-instagram-link">
          <Instagram size={size} />
        </a>
      )}
      {socials.youtube && (
        <a href={socials.youtube} target="_blank" rel="noopener noreferrer"
          className="p-1.5 rounded-full bg-white/5 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
          data-testid="artist-youtube-link">
          <Youtube size={size} />
        </a>
      )}
      {socials.spotify && (
        <a href={socials.spotify} target="_blank" rel="noopener noreferrer"
          className="p-1.5 rounded-full bg-white/5 hover:bg-green-500/20 text-zinc-500 hover:text-green-400 transition-all"
          data-testid="artist-spotify-link">
          <svg className={`w-[${size}px] h-[${size}px]`} width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
        </a>
      )}
    </div>
  );
}

export function LabelPagesSection({ labelPages }) {
  return (
    <div data-testid="label-pages-section" className="mt-8">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Share2 size={18} className="text-yellow-400" /> Nos Pages
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {labelPages.map((page, i) => (
          <a key={i} href={page.url} target="_blank" rel="noopener noreferrer"
            data-testid={`label-page-${page.name.toLowerCase().replace(/\s+/g, '-')}-${page.platform}`}
            className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-zinc-800 hover:border-zinc-600 transition-all group">
            {page.platform === 'facebook' ? (
              <Facebook size={20} className="text-blue-400 group-hover:text-blue-300" />
            ) : (
              <Instagram size={20} className="text-pink-400 group-hover:text-pink-300" />
            )}
            <div>
              <p className="text-white text-sm font-semibold">{page.name}</p>
              <p className="text-zinc-500 text-xs capitalize">{page.platform}</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
